import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BRAND } from "./data";
import { Reveal } from "./Reveal";
import { useContent } from "./ContentContext";
import { useTx, txtStyle } from "./siteText";
import { useLightbox } from "./Lightbox";

export const MenuSection = () => {
  const [tab, setTab] = useState("eats");
  const navigate = useNavigate();
  const { settings, resolveImg } = useContent();
  const tx = useTx();
  const { show } = useLightbox();
  const items = (settings.topPicks && settings.topPicks[tab]) || [];
  const TABS = [
    { id: "eats", label: tx("menu.tabEats").text },
    { id: "drinks", label: tx("menu.tabDrinks").text },
  ];

  return (
    <section id="menu" className="relative py-24 md:py-32 bg-cantina-cream" data-testid="menu-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <Reveal className="text-center flex flex-col items-center mb-10">
          <p className="font-script text-cantina-terracotta mb-2" style={txtStyle(tx("menu.eyebrow"))}>{tx("menu.eyebrow").text}</p>
          <h2 className="font-display tracking-tight text-cantina-ink" style={txtStyle(tx("menu.heading"))}>{tx("menu.heading").text}</h2>
        </Reveal>

        <div className="flex items-center justify-center mb-14">
          <div className="inline-flex bg-cantina-alt rounded-full p-1.5 border border-cantina-line">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="relative px-7 py-2.5 rounded-full font-sans tracking-wide transition-colors"
                style={{ color: tab === t.id ? "#FDFBF7" : "#17233B", ...txtStyle(tx(t.id === "eats" ? "menu.tabEats" : "menu.tabDrinks")) }} data-testid={`menu-tab-${t.id}`}>
                {tab === t.id && <motion.span layoutId="menu-pill" className="absolute inset-0 bg-cantina-blue rounded-full" transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} />}
                <span className="relative z-10">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {items.map((item, i) => (
              <div key={i} className="dish-card bg-cantina-surface border border-cantina-line rounded-2xl overflow-hidden shadow-[0_6px_18px_rgba(23,35,59,0.04)]" data-testid={`menu-item-${tab}-${i}`}>
                <button onClick={() => show(items, i)} className="frame-img aspect-[16/10] bg-cantina-alt cursor-zoom-in block w-full" data-testid={`menu-img-${tab}-${i}`} aria-label={`View ${item.name}`}>
                  <img src={resolveImg(item.img)} alt={item.name} loading="lazy" className="w-full h-full object-cover" />
                </button>
                <div className="p-6">
                  <div className="flex items-baseline justify-between gap-4 mb-2">
                    <h3 className="font-display text-cantina-ink" style={txtStyle(tx("menu.itemTitle"))}>{item.name}</h3>
                    <span className="font-sans text-cantina-chili shrink-0" style={txtStyle(tx("menu.itemPrice"))}>{item.price}</span>
                  </div>
                  <p className="font-sans leading-relaxed text-cantina-ink/60" style={txtStyle(tx("menu.itemDesc"))}>{item.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex flex-wrap justify-center gap-4">
          <button onClick={() => navigate("/menu")} className="btn-outline font-sans tracking-wide text-cantina-ink border-2 border-cantina-ink px-8 py-[14px] rounded-full" style={txtStyle(tx("menu.viewFull"))} data-testid="menu-viewfull-button">
            {tx("menu.viewFull").text}
          </button>
          <a href={BRAND.orderUrl} target="_blank" rel="noopener noreferrer" className="btn-primary bg-cantina-blue text-cantina-cream font-sans tracking-wide px-8 py-4 rounded-full" style={txtStyle(tx("menu.orderLabel"))} data-testid="menu-order-button">
            {tx("menu.orderLabel").text}
          </a>
        </div>
      </div>
    </section>
  );
};
